import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@pkg/evm'
import { ApiPassphraseRequiredException } from '../../errors'
import type { FetchApiData } from '../../types'
import { hibachiAccountCategory, networkEvmChainIds } from '../constants'
import { createClient } from '../createClient'
import { capitalHistoryResponseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiPassphrase,
  config,
  createHeaders,
  proxy,
}) => {
  // apiPassphrase は accountId として使用
  if (!apiPassphrase) {
    throw new ApiPassphraseRequiredException()
  }

  const client = createClient({
    apiKey,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/capital/history',
    query: {
      accountId: apiPassphrase,
    },
    responseSchema: capitalHistoryResponseSchema,
  })

  // timestampSec でソート（昇順）
  const sortedTransactions = response.transactions.sort(
    (a, b) => a.timestampSec - b.timestampSec,
  )

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedTransactions) {
    // completed 以外はスキップ
    if (item.status !== 'completed') {
      continue
    }

    // timestampSec を milliseconds に変換
    const timestampMs = item.timestampSec * 1000

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined
        ? timestampMs
        : Math.min(fetchedFrom, timestampMs)
    fetchedTo =
      fetchedTo === undefined ? timestampMs : Math.max(fetchedTo, timestampMs)

    // Create EVM source for deposits with transaction hash
    if (item.transactionType === 'deposit' && 'transactionHash' in item) {
      const txHash = item.transactionHash
      if (txHash && isHex(txHash)) {
        // Hibachi は Arbitrum で動作
        const evmChainId = networkEvmChainIds.ARBITRUM
        if (evmChainId) {
          sources.push(
            createEvmActionSource({
              chainId: evmChainId,
              transactionHash: txHash,
            }),
          )
        }
      }
    }

    // Create source identifier for each transaction
    const source = `${hibachiAccountCategory}_${config.type}_${item.transactionType}_${item.id}`
    sources.push(source)
  }

  return {
    actions,
    count: response.transactions.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
